import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { AiSummaryResponse } from './types';

/**
 * Hook to read cached AI narrative summary from GET /api/v2/invoices/{public_token}/ai-summary/
 */
export function useAiSummary(publicToken?: string) {
  return useQuery<AiSummaryResponse>({
    queryKey: ['ai-summary', publicToken],
    queryFn: async () => {
      if (!publicToken) throw new Error('Public token is required');
      const response = await api.get<AiSummaryResponse>(
        `/v2/invoices/${publicToken}/ai-summary/`
      );
      return response.data;
    },
    enabled: Boolean(publicToken),
    staleTime: 5 * 60 * 1000, // 5 min cache
  });
}

/**
 * Hook to generate/regenerate AI summary via POST /api/v2/invoices/{public_token}/ai-summary/
 */
export function useGenerateAiSummary() {
  const queryClient = useQueryClient();

  return useMutation<
    AiSummaryResponse,
    Error,
    { publicToken: string; force?: boolean }
  >({
    mutationFn: async ({ publicToken, force = false }) => {
      const params = force ? { force: 'true' } : undefined;
      const response = await api.post<AiSummaryResponse>(
        `/v2/invoices/${publicToken}/ai-summary/`,
        null,
        { params }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(['ai-summary', variables.publicToken], data);
      queryClient.invalidateQueries({ queryKey: ['invoice', variables.publicToken] });
    },
  });
}
